import { useState, useCallback, useEffect } from "react";
import { MinusIcon, CrossIcon } from "../../../assets/icons";
import { updateLayer, getLayer } from "../../../api";
import { toast } from "react-toastify";
import LayerEditSection from "./LayerEditSection/LayerEditSection";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../Context/AppContext";
import "./LayerEditModal.scss";
import { attributeSelectOption } from "../../../constants/acronyms";

const DEFAULT_RULE_COLOR = "#4d504f";

function normalizeColorByAttribute(cba, colors) {
  if (Array.isArray(colors) && colors.length > 0) {
    const attribute = String(colors[0]?.key ?? "").trim();
    return {
      attribute,
      rules: colors.map((entry) => ({
        value: entry?.value != null ? String(entry.value) : "",
        color: entry?.color || DEFAULT_RULE_COLOR,
      })),
    };
  }
  if (!cba || typeof cba !== "object") {
    return { attribute: "", rules: [] };
  }
  return {
    attribute: typeof cba.attribute === "string" ? cba.attribute : "",
    rules: Array.isArray(cba.rules)
      ? cba.rules.map((r) => ({
          value: r?.value != null ? String(r.value) : "",
          color: r?.color || DEFAULT_RULE_COLOR,
        }))
      : [],
  };
}

function getFeaturePropertyKeys(features) {
  if (!Array.isArray(features) || features.length === 0) return [];
  const keys = new Set();
  for (const feature of features) {
    const props = feature?.properties;
    if (!props || typeof props !== "object") continue;
    Object.keys(props).forEach((key) => keys.add(key));
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
}

function buildColorsPayload(matchData) {
  const key = matchData.colorByAttribute?.attribute?.trim();
  if (!key) return [];

  return (matchData.colorByAttribute?.rules || [])
    .filter((r) => r && String(r.value ?? "").trim() !== "")
    .map((r) => ({
      key,
      value: String(r.value).trim(),
      color: r.color || DEFAULT_RULE_COLOR,
    }));
}

function buildStylePayload(matchData) {
  const stroke = { color: matchData.color, width: 1 };
  const attr = matchData.colorByAttribute?.attribute?.trim();
  if (!attr) {
    return { stroke };
  }
  const rules = (matchData.colorByAttribute?.rules || [])
    .filter((r) => r && String(r.value ?? "").trim() !== "")
    .map((r) => ({
      value: String(r.value).trim(),
      color: r.color || DEFAULT_RULE_COLOR,
    }));
  return {
    stroke,
    colorByAttribute: {
      attribute: attr,
      rules,
    },
  };
}

const LayerEditModal = ({
  title,
  selectedComune,
  currentLayerData,
  closeModal,
}) => {
  const [matchData, setMatchData] = useState({
    id: currentLayerData?.id || "NA",
    chiave: currentLayerData?.chiave || "",
    tipo: currentLayerData?.tipo || "",
    selectedCrs: {
      value: currentLayerData?.crs || "EPSG:32633",
      label: currentLayerData?.crs || "EPSG:32633",
    },
    comuneId: selectedComune?.id,
    crs: currentLayerData?.crs || "EPSG:32633",
    color: currentLayerData?.style?.stroke?.color || "rgb(77, 80, 79)",
    colorByAttribute: normalizeColorByAttribute(
      currentLayerData?.style?.colorByAttribute,
      currentLayerData?.colors
    ),
  });
  const navigate = useNavigate();
  const { setLayers, mapViewTarget, setMapViewTarget } = useAppContext();
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);

  useEffect(() => {
    if (!currentLayerData?.id) return;
    setMatchData({
      id: currentLayerData.id,
      chiave: currentLayerData.chiave || "",
      tipo: currentLayerData.tipo || "",
      selectedCrs: {
        value: currentLayerData.crs || "EPSG:32633",
        label: currentLayerData.crs || "EPSG:32633",
      },
      comuneId: selectedComune?.id,
      crs: currentLayerData.crs || "EPSG:32633",
      color: currentLayerData?.style?.stroke?.color || "rgb(77, 80, 79)",
      colorByAttribute: normalizeColorByAttribute(
        currentLayerData?.style?.colorByAttribute,
        currentLayerData?.colors
      ),
    });
  }, [currentLayerData, selectedComune?.id]);

  useEffect(() => {
    let cancelled = false;
    const layerId = currentLayerData?.id;

    const loadAttributeOptions = async () => {
      if (!layerId || layerId === "NA") {
        setAttributeOptions([]);
        return;
      }

      setIsLoadingAttributes(true);
      try {
        const data = await getLayer(layerId);
        if (cancelled) return;
        const keys = getFeaturePropertyKeys(data?.features);
        setAttributeOptions(keys.map((key) => attributeSelectOption(key)));

        if (Array.isArray(data?.colors) && data.colors.length > 0) {
          setMatchData((prev) => ({
            ...prev,
            colorByAttribute: normalizeColorByAttribute(null, data.colors),
          }));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading layer properties:", error);
          toast.error("Impossibile caricare le proprietà del layer");
          setAttributeOptions([]);
        }
      } finally {
        if (!cancelled) setIsLoadingAttributes(false);
      }
    };

    loadAttributeOptions();
    return () => {
      cancelled = true;
    };
  }, [currentLayerData?.id]);

  const handleSelectCrsChange = useCallback((selectedCrs) => {
    setMatchData((prev) => ({ ...prev, selectedCrs, crs: selectedCrs.value }));
  }, []);

  const handleInputChange = useCallback((ev) => {
    setMatchData((prev) => ({ ...prev, [ev.target.name]: ev.target.value }));
  }, []);

  const handleColorByAttributeChange = useCallback((next) => {
    setMatchData((prev) => ({
      ...prev,
      colorByAttribute:
        typeof next === "function"
          ? next(prev.colorByAttribute)
          : next,
    }));
  }, []);

  const handleLayerUpdate = useCallback(async () => {
    try {
      await updateLayer({
        id: matchData.id,
        style: buildStylePayload(matchData),
        colors: buildColorsPayload(matchData),
        crs: matchData.crs,
        chiave: matchData.chiave,
        tipo: matchData.tipo,
        comuneId: matchData.comuneId,
      });

      toast.success(`Layer aggiornato con successo:`);

      setLayers([]);

      const currentTarget = { ...mapViewTarget } || {
        layer: selectedComune?.nome === "ITALIA" ? "comuni" : "limiti",
        comune: selectedComune,
      };

      setTimeout(() => {
        setMapViewTarget(currentTarget);
        navigate("/MapView");
        closeModal();
      }, 20);
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  }, [
    matchData,
    closeModal,
    setLayers,
    mapViewTarget,
    setMapViewTarget,
    navigate,
    selectedComune,
  ]);

  return (
    <section className="layer-edit-modal-container">
      <header className="modal-header">
        <div className="modal-header-content">
          <h1 className="modal-title">{title}</h1>
          <div className="modal-icons">
            <div className="modal-icon" onClick={closeModal}>
              <MinusIcon />
            </div>
            <div className="modal-icon" onClick={closeModal}>
              <CrossIcon />
            </div>
          </div>
        </div>
      </header>

      <div className="layer-edit-modal-body">
        <LayerEditSection
          selectedComune={selectedComune}
          chiave={matchData?.chiave}
          tipo={matchData?.tipo}
          selectedCrs={matchData?.selectedCrs}
          color={matchData?.color}
          handleCrsChange={handleSelectCrsChange}
          handleInputChange={handleInputChange}
          colorByAttribute={matchData.colorByAttribute}
          onColorByAttributeChange={handleColorByAttributeChange}
          attributeOptions={attributeOptions}
          isLoadingAttributes={isLoadingAttributes}
        />
      </div>

      {/* footer */}
      <footer className="modal-footer">
        <button className="modal-footer-button" onClick={handleLayerUpdate}>
          Save
        </button>
      </footer>
    </section>
  );
};

export default LayerEditModal;
